import dayjs from "dayjs"
import React from "react"

import { Button } from "../../components/shared/Buttons"
import EventTypeCard from "../../components/shared/EventTypeCard"
import { Label, Select, ToggleButtonGroup } from "../../components/shared/Inputs"
import { Divider } from "../../components/shared/Layout"
import { AnimatedModal } from "../../components/shared/Modal"
import Typography from "../../components/shared/Typography"
import { useCalendarFlyout } from "../../contexts/CalendarFlyoutContext"
import { useToast } from "../../contexts/ToastContext"
import { useBookableEvents } from "../../pages/BookableEvents/BookableEventsContext"
import { useBookableEventTypes } from "../../pages/BookableEventTypes/BookableEventTypesContext"

import BookableEventFormDetails from "./BookableEventFormDetails"
import BookableEventRegistrants from "./BookableEventRegistrants"

export default function BookableEventForm({ onBookableEventChanged, onDeleteBookableEvent }) {
  const {
    bookableEvent,
    bookableEventTab,
    setBookableEventTab,
    setBookableEvent,
    fetching,
    setDeleteModalVisible,
    upsertBookableEvent,
    deleteBookableEvent,
    deleteModalVisible
  } = useBookableEvents()
  const { bookableEventTypes } = useBookableEventTypes()
  const { setShowFlyout } = useCalendarFlyout()
  const { showToast } = useToast()

  const editingExistingEvent = !!bookableEvent.id
  const selectedEventType = bookableEventTypes?.find(
    (et) => et.id.toString() === bookableEvent.bookableEventTypeId?.toString()
  )
  const saveEnabled = !fetching && selectedEventType

  // Handle event type selection
  const handleEventTypeChange = (eventTypeId) => {
    if (eventTypeId) {
      const eventType = bookableEventTypes?.find((et) => et.id.toString() === eventTypeId.toString())
      const dates = calculateInitialDates(eventType)

      setBookableEvent((prev) => ({
        ...prev,
        bookableEventTypeId: eventTypeId,
        startsAt: dates.startsAt,
        endsAt: dates.endsAt,
        allDay: false
      }))
    } else {
      setBookableEvent((prev) => ({ ...prev, bookableEventTypeId: null }))
    }
  }

  // Calculate initial dates based on event type
  const calculateInitialDates = (eventType) => {
    const startsAt = dayjs().add(1, "hour").startOf("hour").toDate()
    let endsAt

    if (eventType?.durationUnit === "day" && eventType?.duration) {
      // For multi-day events
      endsAt = dayjs(startsAt)
        .add(eventType.duration - 1, "day")
        .toDate()
    } else if (eventType?.durationUnit === "hour" && eventType?.duration) {
      // For hour-based events
      endsAt = dayjs(startsAt).add(eventType.duration, "hour").toDate()
    } else {
      // Default for minute-based events
      const durationInMinutes = eventType?.duration || 60
      endsAt = dayjs(startsAt).add(durationInMinutes, "minute").toDate()
    }

    return { startsAt, endsAt }
  }

  const handleSave = () => {
    upsertBookableEvent({ ...bookableEvent }).then((result) => {
      if (result?.data?.upsertBookableEvent?.result === "success") {
        const newEvent = result.data.upsertBookableEvent.bookableEvent
        onBookableEventChanged(newEvent)
        setBookableEvent(newEvent)
        setShowFlyout(false)
        showToast(`Your event was saved successfully`)
      } else {
        console.error(result)
        let errorMessage = "There was an error saving your event"
        if (result.data?.upsertBookableEvent?.errors) errorMessage += `: ${result.data.upsertBookableEvent.errors}`
        showToast({ type: "error", content: errorMessage })
      }
    })
  }

  return (
    <>
      {/* Event Type card and toggle button group */}
      {bookableEvent?.id && (
        <>
          <EventTypeCard
            eventType={bookableEvent.bookableEventType}
            onEditClick={() => {
              window.location.href = `/portal/services#events`
            }}
          />
          <ToggleButtonGroup
            className="my-4"
            value={bookableEventTab}
            onChange={(value) => setBookableEventTab(value)}
            options={[
              { label: "Registrants", value: "registrants" },
              { label: "Details", value: "details" }
            ]}
          />
          <Divider />
        </>
      )}

      {!editingExistingEvent && (
        <div className="mb-4 flex flex-col">
          {bookableEventTypes && bookableEventTypes.length > 0 ? (
            <div className="mb-4">
              <Label htmlFor="bookable-event-type">Group event</Label>
              <Select
                id="bookable-event-type"
                disabled={!!bookableEvent.id}
                value={bookableEvent.bookableEventTypeId || ""}
                onChange={(e) => handleEventTypeChange(e.target.value)}>
                <option value="">Select</option>
                {bookableEventTypes.map((eventType) => (
                  <option key={eventType.id} value={eventType.id}>
                    {eventType.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : (
            <Typography variant="body">
              No event types available. Please{" "}
              <a href="/portal/services#events" className="text-teal hover:underline">
                create an event type in the Services Page
              </a>
              .
            </Typography>
          )}

          {selectedEventType && (
            <EventTypeCard
              eventType={selectedEventType}
              onEditClick={() => {
                window.location.href = `/portal/services#events`
              }}
            />
          )}
        </div>
      )}

      {/* Bookable Event Form and Registrants */}
      {bookableEventTab === "details" || !editingExistingEvent ? (
        <BookableEventFormDetails />
      ) : bookableEvent?.id && bookableEventTab === "registrants" ? (
        <BookableEventRegistrants />
      ) : null}

      <Divider />

      {/* Save/Delete buttons */}
      <div className={`mt-4 flex w-full items-center ${editingExistingEvent ? "justify-between" : "justify-end"}`}>
        {editingExistingEvent && (
          <Button type="warning" onClick={() => setDeleteModalVisible(true)} disabled={fetching}>
            Delete
          </Button>
        )}
        <div className="flex gap-4">
          <Button type="primary" onClick={handleSave} disabled={fetching || !saveEnabled}>
            Save
          </Button>
        </div>
      </div>

      {/* Delete Modal */}
      <AnimatedModal
        visible={deleteModalVisible}
        header="Delete Group Event"
        showFooter={true}
        actionButtonCopy="Delete"
        cancelButtonCopy="Cancel"
        actionButtonType="destructive"
        cancelButtonType="tertiary"
        onSave={() => {
          deleteBookableEvent({ id: bookableEvent.id }).then((result) => {
            if (result?.data?.deleteBookableEvent?.result === "success") {
              showToast("Event deleted successfully")
              if (onDeleteBookableEvent) {
                onDeleteBookableEvent(bookableEvent)
              }
              setDeleteModalVisible(false)
              if (setShowFlyout) {
                setShowFlyout(false)
              }
            } else {
              showToast({
                type: "error",
                content: `There was an error deleting your event: ${result?.data?.deleteBookableEvent?.errors}`
              })
            }
          })
        }}
        hideModal={() => setDeleteModalVisible(false)}>
        <p>Are you sure you want to delete this event? This action cannot be undone.</p>
      </AnimatedModal>
    </>
  )
}
